import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  CheckCircle2,
  Download,
  Globe2,
  ImagePlus,
  KeyRound,
  LoaderCircle,
  PackagePlus,
  Puzzle,
  Save,
  Trash2,
  Unplug
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import type { CollectedProduct, ExtensionInboxItem } from "../lib/types";
import { dateTime, money } from "../lib/format";
import { useI18n } from "../lib/i18n";

type CollectionTab = "link" | "extension";

function ExtensionThumbnail({ url, title }: { url?: string; title: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="extension-inbox-image">
      {url && !failed ? (
        <img src={url} alt={title} onError={() => setFailed(true)} />
      ) : (
        <ImagePlus size={26} />
      )}
    </div>
  );
}

export function CollectionPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [tab, setTab] = useState<CollectionTab>("link");
  const [url, setUrl] = useState("");
  const [collected, setCollected] = useState<CollectedProduct | null>(null);
  const [notice, setNotice] = useState("");
  const [pairingCode, setPairingCode] = useState("");

  const inspect = useMutation({
    mutationFn: () => api.post<{ collected: CollectedProduct }>("/api/catalog/collect", { url }),
    onSuccess: (result) => {
      setCollected(result.collected);
      setNotice(t("采集完成，可以检查后导入商品库"));
    },
    onError: (error) => setNotice(error.message)
  });
  const importProduct = useMutation({
    mutationFn: () => {
      if (!collected) throw new Error("没有可导入的采集结果");
      return api.post<{ product: { id: string } }>("/api/catalog/collect/import", collected);
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["catalog-products"] });
      navigate(`/products/${result.product.id}`);
    },
    onError: (error) => setNotice(error.message)
  });

  const extensionStatus = useQuery({
    queryKey: ["extension-status"],
    queryFn: () =>
      api.get<{
        devices: Array<{
          id: string;
          name: string;
          last_seen_at: string | null;
          revoked_at: string | null;
          created_at: string;
          updated_at: string;
        }>;
        pendingCount: number;
      }>("/api/extensions/status")
  });
  const inboxQuery = useQuery({
    queryKey: ["extension-inbox"],
    queryFn: () =>
      api.get<{ items: ExtensionInboxItem[] }>("/api/extensions/inbox?limit=100")
  });
  const createPairing = useMutation({
    mutationFn: () =>
      api.post<{ code: string; expiresAt: string; expiresInSeconds: number }>(
        "/api/extensions/pairing-codes"
      ),
    onSuccess: (result) => {
      setPairingCode(result.code);
      setNotice("配对码已生成，请在浏览器扩展中输入");
    },
    onError: (error) => setNotice(error.message)
  });
  const importInbox = useMutation({
    mutationFn: (id: string) =>
      api.post<{ productId: string }>(`/api/extensions/inbox/${id}/import`),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["extension-inbox"] }),
        queryClient.invalidateQueries({ queryKey: ["extension-status"] }),
        queryClient.invalidateQueries({ queryKey: ["catalog-products"] })
      ]);
      navigate(`/products/${result.productId}`);
    },
    onError: (error) => setNotice(error.message)
  });
  const discardInbox = useMutation({
    mutationFn: (id: string) => api.delete(`/api/extensions/inbox/${id}`),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["extension-inbox"] }),
        queryClient.invalidateQueries({ queryKey: ["extension-status"] })
      ]);
    }
  });
  const revokeDevice = useMutation({
    mutationFn: (id: string) => api.delete(`/api/extensions/devices/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["extension-status"] });
    }
  });

  const inboxItems = inboxQuery.data?.items ?? [];
  const devices = extensionStatus.data?.devices ?? [];

  async function copyPairingCode() {
    if (!pairingCode) return;
    await navigator.clipboard.writeText(pairingCode);
    setNotice("配对码已复制");
  }

  return (
    <div className="collection-page">
      <section className="panel collection-hero">
        <div className="collection-hero-copy">
          <span className="eyebrow">{t("产品采集")}</span>
          <h2>{t("链接采集与浏览器插件采集双通道")}</h2>
          <p>
            {t(
              "对公开页面使用链接采集；对需要登录态、容易触发验证的 1688、淘宝、天猫和拼多多页面，使用浏览器插件从当前页面采集。"
            )}
          </p>
        </div>
        <Globe2 size={48} />
      </section>

      <div className="editor-tabs collection-tabs">
        <button
          type="button"
          className={tab === "link" ? "editor-tab active" : "editor-tab"}
          onClick={() => setTab("link")}
        >
          <Download size={15} />
          {t("链接采集")}
        </button>
        <button
          type="button"
          className={tab === "extension" ? "editor-tab active" : "editor-tab"}
          onClick={() => setTab("extension")}
        >
          <Puzzle size={15} />
          {t("浏览器插件采集")}
          {extensionStatus.data?.pendingCount ? (
            <span className="tab-count">{extensionStatus.data.pendingCount}</span>
          ) : null}
        </button>
      </div>

      {notice ? <div className="notice success-notice">{t(notice)}</div> : null}

      {tab === "link" ? (
        <>
          <section className="panel collection-input-panel">
            <div className="panel-header">
              <div>
                <h2>{t("链接采集")}</h2>
                <span>{t("粘贴公开商品链接，系统会读取标题、描述、价格和主图。")}</span>
              </div>
            </div>
            <div className="collection-input-row">
              <input
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                placeholder="https://detail.1688.com/offer/..."
              />
              <button
                type="button"
                className="primary-button"
                disabled={!url || inspect.isPending}
                onClick={() => {
                  setNotice("");
                  inspect.mutate();
                }}
              >
                {inspect.isPending ? <LoaderCircle className="spin" size={16} /> : <Download size={16} />}
                {inspect.isPending ? t("采集中...") : t("开始采集")}
              </button>
            </div>
            {inspect.isError ? <div className="notice error-notice">{inspect.error.message}</div> : null}
          </section>

          {collected ? (
            <section className="panel collection-preview">
              <div className="panel-header">
                <div>
                  <h2>{t("采集预览")}</h2>
                  <span>{t("来源")}：{collected.sourcePlatform} · {collected.sourceUrl}</span>
                </div>
                <button
                  type="button"
                  className="primary-button"
                  disabled={importProduct.isPending}
                  onClick={() => importProduct.mutate()}
                >
                  <Save size={16} />
                  {importProduct.isPending ? t("导入中...") : t("导入商品库并编辑")}
                </button>
              </div>
              <div className="collection-preview-grid">
                <div className="collection-preview-image">
                  {collected.images[0] ? (
                    <img src={collected.images[0]} alt={collected.title} />
                  ) : (
                    <ImagePlus size={36} />
                  )}
                </div>
                <div className="collection-preview-copy">
                  <h3>{collected.title}</h3>
                  <p>{collected.description || t("采集结果暂无描述")}</p>
                  <div className="collection-meta">
                    <span>{t("采集价")} <strong>{money(collected.price, collected.currency)}</strong></span>
                    <span>{t("图片")} <strong>{collected.images.length}</strong></span>
                  </div>
                  <div className="collection-image-strip">
                    {collected.images.slice(0, 8).map((image) => (
                      <img key={image} src={image} alt="" />
                    ))}
                  </div>
                </div>
              </div>
              <div className="collection-next">
                <span>{t("下一步")}</span>
                <ArrowRight size={16} />
                <strong>{t("补充类目、SKU、售价和库存")}</strong>
              </div>
            </section>
          ) : null}
        </>
      ) : (
        <>
          <section className="panel extension-pairing-panel">
            <div className="panel-header">
              <div>
                <h2>{t("连接浏览器采集扩展")}</h2>
                <span>
                  {t(
                    "在 Chrome 或 Edge 加载 extensions/erp-collector 目录，然后使用配对码连接。扩展不会读取或上传 Cookie。"
                  )}
                </span>
              </div>
              <button
                type="button"
                className="primary-button"
                disabled={createPairing.isPending}
                onClick={() => createPairing.mutate()}
              >
                <KeyRound size={16} />
                {createPairing.isPending ? t("生成中...") : t("生成配对码")}
              </button>
            </div>
            {pairingCode ? (
              <button type="button" className="pairing-code" onClick={() => void copyPairingCode()}>
                <span>{pairingCode}</span>
                <small>{t("点击复制，配对码 10 分钟内有效")}</small>
              </button>
            ) : (
              <div className="extension-empty-inline">
                <KeyRound size={18} />
                <span>{t("尚未生成配对码")}</span>
              </div>
            )}
            {devices.length > 0 ? (
              <div className="extension-device-list">
                {devices.map((device) => (
                  <div className="extension-device-row" key={device.id}>
                    <CheckCircle2 size={17} />
                    <div>
                      <strong>{device.name}</strong>
                      <small>
                        {device.revoked_at
                          ? t("已撤销")
                          : `${t("最近连接")}：${
                              device.last_seen_at ? dateTime(device.last_seen_at) : t("尚未采集")
                            }`}
                      </small>
                    </div>
                    {!device.revoked_at ? (
                      <button
                        type="button"
                        className="icon-button danger-button"
                        title={t("撤销设备")}
                        onClick={() => revokeDevice.mutate(device.id)}
                      >
                        <Unplug size={15} />
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
          </section>

          <section className="panel extension-inbox-panel">
            <div className="panel-header">
              <div>
                <h2>{t("插件采集箱")}</h2>
                <span>{t("浏览器插件采集的商品会先进入这里，去重后再导入商品库。")}</span>
              </div>
              <span className="result-count">{t("待处理")}：{inboxItems.filter((item) => item.status === "pending").length}</span>
            </div>
            {inboxQuery.isLoading ? (
              <div className="loading-inline"><LoaderCircle className="spin" size={16} />{t("加载中...")}</div>
            ) : null}
            {inboxItems.length === 0 && !inboxQuery.isLoading ? (
              <div className="empty-state compact-empty-state">
                <Puzzle size={28} />
                <strong>{t("插件采集箱为空")}</strong>
                <span>{t("打开货源商品页，点击右下角“采集到 ERP”。")}</span>
              </div>
            ) : null}
            <div className="extension-inbox-list">
              {inboxItems.map((item) => (
                <article className={`extension-inbox-card status-${item.status}`} key={item.id}>
                  <ExtensionThumbnail url={item.images[0]} title={item.title} />
                  <div className="extension-inbox-copy">
                    <div className="extension-inbox-title">
                      <span className={`platform-dot dot-${item.source_platform}`} />
                      <strong>{item.title}</strong>
                    </div>
                    <span className="table-subtext">
                      {item.source_platform} · {item.source_product_id || item.source_url}
                    </span>
                    <div className="collection-meta">
                      <span>{t("采集价")} <strong>{money(item.price, item.currency)}</strong></span>
                      <span>{t("图片")} <strong>{item.images.length}</strong></span>
                      <span>{t("SKU")} <strong>{item.skus.length}</strong></span>
                    </div>
                  </div>
                  <div className="extension-inbox-actions">
                    {item.status === "imported" && item.product_id ? (
                      <button
                        type="button"
                        className="ghost-button compact-button"
                        onClick={() => navigate(`/products/${item.product_id}`)}
                      >
                        {t("查看商品")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="primary-button compact-button"
                        disabled={importInbox.isPending || item.status === "discarded"}
                        onClick={() => importInbox.mutate(item.id)}
                      >
                        <PackagePlus size={15} />
                        {t("导入商品库")}
                      </button>
                    )}
                    <button
                      type="button"
                      className="icon-button danger-button"
                      title={t("忽略")}
                      disabled={discardInbox.isPending || item.status === "discarded"}
                      onClick={() => discardInbox.mutate(item.id)}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
